import React, { useState, lazy, Suspense, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Modal,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { ArrowBack, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Lazy load your component files
const LegislativePage = lazy(() => import("./LegislativePage"));
const CommunityEngagementPage = lazy(() => import("./CommunityEngagementPage"));
const ConservationPage = lazy(() => import("./ConservationPage"));
const AutomationPage = lazy(() => import("./AutomationPage"));

// Animations
const slideInFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-300px) translateY(var(--target-y)) rotate(-10deg);
  }
  60% {
    opacity: 1;
    transform: translateX(20px) translateY(var(--target-y)) rotate(2deg);
  }
  100% {
    opacity: 1;
    transform: translateX(0) translateY(var(--target-y)) rotate(0deg);
  }
`;

const slideVideoCenter = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8) rotateY(90deg);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) rotateY(0deg);
  }
`;

const moveLogoRight = keyframes`
  from {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);
    width: 60%;
    height: 60%;
  }
  to {
    top: 50%;
    right: 5%;
    left: auto;
    transform: translate(0, -50%) scale(0.5);
    width: 25%;
    height: 40%;
  }
`;

const rippleEffect = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7);
  }
  50% {
    box-shadow: 0 0 0 15px rgba(76, 175, 80, 0), 0 0 0 30px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0), 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

// Styled Components
const PageWrapper = styled(Box)({
  minHeight: "100vh",
  backgroundImage: "url('images/wildlife-background.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    zIndex: 1,
  },
});

const CenteredLogo = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  height: "60%",
  backgroundImage: "url('images/WildlifeLogo.png')",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundColor: "#F5F5DC",
  cursor: "pointer",
  borderRadius: "25px",
  boxShadow:
    "0 25px 50px rgba(76, 175, 80, 0.3), 0 0 0 15px rgba(255, 255, 255, 0.1)",
  zIndex: 10,
  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translate(-50%, -50%) scale(1.05)",
    boxShadow:
      "0 30px 60px rgba(76, 175, 80, 0.4), 0 0 0 15px rgba(255, 255, 255, 0.2)",
    animation: `${rippleEffect} 2s ease-in-out infinite`,
  },
  "&.moved-right": {
    animation: `${moveLogoRight} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
    cursor: "default",
  },
});

const VideoContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "45%",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "0 0 0 0 rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
  backgroundColor: "#000",
  opacity: 0,
  zIndex: 5,
  "&.visible": {
    animation: `${slideVideoCenter} 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards 0.5s`,
  },
});

const VideoWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  paddingBottom: "56.25%",
  backgroundColor: "#000",
});

const VideoElement = styled("video")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "block",
  backgroundColor: "#000",
});

const VideoTitle = styled(Typography)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "12px 16px",
  background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
  color: "white",
  fontSize: "1rem",
  fontWeight: 600,
  zIndex: 5,
});

const SectionsContainer = styled(Box)({
  position: "absolute",
  left: "5%",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 6,
  display: "flex",
  flexDirection: "column",
  // gap: "20px",
  opacity: 0,
  "&.visible": {
    opacity: 1,
    // transition: "opacity 0.5s ease 1.2s",
  },
});

const CategoryCard = styled(Card)(({ index }) => {
  const positions = [
    { y: "20px", delay: 1.5 },
    { y: "30px", delay: 1.7 },
    { y: "40px", delay: 1.9 },
    { y: "50px", delay: 2.1 },
  ];

  const pos = positions[index] || positions[0];

  return {
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    width: "280px",
    minHeight: "120px",
    opacity: 0,
    transform: "translateX(-300px)",
    animation: `${slideInFromLeft} 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards ${pos.delay}s`,
    "--target-y": pos.y,
    position: "relative",
    overflow: "hidden",

    "&::after": {
      content: '""',
      position: "absolute",
      top: "-50%",
      left: "-50%",
      width: "200%",
      height: "200%",
      transform: "scale(0)",
      transition: "transform 0.6s ease",
    },

    "&:hover": {
      transform: "translateX(15px) translateY(var(--target-y)) scale(1.05)",
      boxShadow:
        " 0 0 0 0 rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
      "&::after": {
        transform: "scale(1)",
      },
    },
  };
});

const IconBox = styled(Box)({
  fontSize: "2.5rem",
  marginBottom: "8px",
  transition: "transform 0.4s ease",
  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
  ".MuiCard-root:hover &": {
    transform: "scale(1.3) rotate(10deg)",
  },
});

const CardTitle = styled(Typography)({
  color: "#fff",
  fontWeight: 700,
  marginBottom: "6px",
  fontSize: "1rem",
  lineHeight: 1.2,
});

const CardDescription = styled(Typography)({
  color: "rgba(255, 255, 255, 0.85)",
  fontSize: "0.85rem",
  lineHeight: 1.3,
});

const BackButton = styled(Button)({
  position: "absolute",
  top: "20px",
  left: "20px",
  zIndex: 1000,
  color: "white",
  border: "2px solid rgba(255, 255, 255, 0.4)",
  padding: "10px 24px",
  borderRadius: "25px",
  fontSize: "0.95rem",
  cursor: "pointer",
  background: "rgba(15, 23, 42, 0.8)",
  backdropFilter: "blur(15px)",
  boxShadow: "0 8px 25px rgba(45, 134, 89, 0.3)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  fontWeight: 600,
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.8)",
    transform: "translateY(-3px)",
    boxShadow:
      " 0 0 0 0 rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
    background: "rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
  },
});

const ModalContent = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "1400px",
  maxHeight: "100vh",
  backgroundColor: "white",
  borderRadius: "20px",
  // padding: "40px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  overflow: "auto",
  outline: "none",
  border: "none",
});

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  height: "400px",
  background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
  borderRadius: "20px",
});

const WildlifePage = () => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = [
    {
      id: "LegislativePage",
      title: "Legislative",
      icon: "⚖️",
      backgroundColor: "rgba(13, 71, 161) ",
    },
    {
      id: "CommunityEngagementPage",
      title: "Community Engagement",
      icon: "🤝",
      backgroundColor: "#F57C00",
    },
    {
      id: "ConservationPage",
      title: "Conservation",
      icon: "🌱",
      backgroundColor: "rgba(27, 94, 32)",
    },
    {
      id: "AutomationPage",
      title: "Automation",
      icon: "🎛️",
      backgroundColor: "rgba(38, 104, 155, 0.85)",
    },
  ];

  const handleLogoClick = () => {
    setShowContent(true);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCategory(null);

    // Stop the video completely
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // reset audio
    }
  };

  const handleBackClick = () => {
    if (showContent) {
      setShowContent(false);
    } else {
      navigate("/");
    }
  };

  // Render the appropriate component based on selected category
  const renderModalContent = () => {
    if (!selectedCategory) return null;

    const categoryComponents = {
      LegislativePage: LegislativePage,
      CommunityEngagementPage: CommunityEngagementPage,
      ConservationPage: ConservationPage,
      AutomationPage: AutomationPage,
    };

    const Component = categoryComponents[selectedCategory.id];

    if (Component) {
      return (
        <Suspense
          fallback={
            <LoadingContainer>
              <CircularProgress
                size={60}
                sx={{ color: selectedCategory.color }}
              />
              <Typography
                variant="h6"
                sx={{ mt: 2, color: selectedCategory.color, fontWeight: 600 }}
              >
                Loading {selectedCategory.title}...
              </Typography>
            </LoadingContainer>
          }
        >
          <Component />
        </Suspense>
      );
    }

    // Fallback for categories without specific components
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#2e7d32", fontWeight: 700, mb: 2 }}
        >
          {selectedCategory.title}
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, color: "#555" }}>
          {selectedCategory.description}
        </Typography>
        <Typography variant="body1" sx={{ color: "#777" }}>
          Component for {selectedCategory.title} is under development.
        </Typography>
      </Box>
    );
  };

  return (
    <PageWrapper sx={{ backgroundImage: "url('./images/wildlife.jpg')" }}>
      <BackButton startIcon={<ArrowBack />} onClick={handleBackClick}>
        Back
      </BackButton>

      {/* Centered Logo with white background */}
      <CenteredLogo
        className={showContent ? "moved-right" : ""}
        onClick={!showContent ? handleLogoClick : undefined}
      />

      {/* Video Container - Center */}
      {showContent && (
        <VideoContainer className="visible">
          <VideoWrapper>
            <VideoElement
              src="videos/Wildlife.mp4"
              controls={true}
              preload="metadata"
              autoPlay
              muted
            />
            <VideoTitle>
              Wildlife Protection & Conservation - Overview
            </VideoTitle>
          </VideoWrapper>
        </VideoContainer>
      )}

      {/* Category Sections - Animated from left */}
      {showContent && (
        <SectionsContainer className="visible">
          {categories.map((category, index) => (
            <CategoryCard
              key={category.id}
              index={index}
              sx={{
                background: category.backgroundColor || "#00BCD4",
              }}
              onClick={() => handleCategoryClick(category)}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                  gap: "8px",
                }}
              >
                <IconBox>{category.icon}</IconBox>
                <CardTitle variant="h6">{category.title}</CardTitle>
                <CardDescription variant="body2">
                  {category.description}
                </CardDescription>
              </CardContent>
            </CategoryCard>
          ))}
        </SectionsContainer>
      )}

      {/* Modal for Category Content */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(5px)",
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.85)",
            },
          },
        }}
        closeAfterTransition
      >
        <ModalContent>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              zIndex: 1300,
              color: "rgba(15, 23, 42, 0.8)",
              border: "2px solid rgba(15, 23, 42, 0.8)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "rotate(90deg) scale(1.1)",
                boxShadow: "0 4px 15px rgba(15, 23, 42, 0.3)",
                borderColor: "rgba(15, 23, 42, 0.8)",
              },
            }}
          >
            <Close />
          </IconButton>

          {renderModalContent()}
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
};

export default WildlifePage;
